import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export const AuditLogTab = ({ data, filters, loading, onFilterChange, t }) => (
  <Card>
    <CardHeader>
      <CardTitle>{t('admin.auditLog')}</CardTitle>
      <CardDescription>{t('admin.auditLogDesc')}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <Select value={filters.entity_type || 'all'} onValueChange={(v) => onFilterChange({ entity_type: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[180px]" data-testid="audit-filter-entity"><SelectValue placeholder={t('admin.auditFilterEntity')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.auditAll')}</SelectItem>
            <SelectItem value="settings">Settings</SelectItem>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="sku">SKU</SelectItem>
            <SelectItem value="material">Material</SelectItem>
            <SelectItem value="request">Request</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.action || 'all'} onValueChange={(v) => onFilterChange({ action: v === 'all' ? '' : v })}>
          <SelectTrigger className="w-[180px]" data-testid="audit-filter-action"><SelectValue placeholder={t('admin.auditFilterAction')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.auditAll')}</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="approve">Approve</SelectItem>
            <SelectItem value="revoke">Revoke</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : data.items.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{t('admin.auditNoEntries')}</p>
      ) : (
        <div className="space-y-2" data-testid="audit-log-list">
          {data.items.map((log) => (
            <div key={log.log_id} className="p-3 border rounded-lg text-sm" data-testid={`audit-row-${log.log_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs font-mono">{log.entity_type}</Badge>
                  <Badge className={
                    log.action === 'create' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    log.action === 'update' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                    log.action === 'delete' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' :
                    log.action === 'approve' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  }>{log.action}</Badge>
                  <span className="text-muted-foreground">{log.user_name || log.user_id}</span>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</span>
              </div>
              {log.entity_id && <p className="text-xs text-muted-foreground mt-1 font-mono">ID: {log.entity_id.substring(0, 12)}...</p>}
              {log.changes && (
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">{t('admin.auditChanges')}</summary>
                  <pre className="text-xs bg-muted/50 p-2 rounded mt-1 overflow-x-auto max-h-32">{JSON.stringify(log.changes, null, 2)}</pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}

      {data.pages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => onFilterChange({ page: data.page - 1 })} data-testid="audit-prev-page">
            <ChevronLeft className="h-4 w-4 mr-1" />{t('admin.auditPrevPage')}
          </Button>
          <span className="text-sm text-muted-foreground">{t('admin.auditPage', { page: data.page, pages: data.pages })}</span>
          <Button variant="outline" size="sm" disabled={data.page >= data.pages} onClick={() => onFilterChange({ page: data.page + 1 })} data-testid="audit-next-page">
            {t('admin.auditNextPage')}<ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);
